import { CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { forwardRef } from 'react';

const useStyles = makeStyles((theme) => ({
    findButton: {
        ...theme.button,
    },
    buttonText: {
        ...theme.typography.buttonText,
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 20,
            paddingRight: 20,
        },
    },
    whiteText: {
        ...theme.typography.buttonText,
        color: 'white',
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 20,
            paddingRight: 20,
        },
    },
    mediumBrown: {
        backgroundColor: theme.palette.common.mediumBrown,
        '&:hover': {
            backgroundColor: theme.palette.common.mediumBrown,
        },
    },
    yellow: {
        backgroundColor: theme.palette.common.yellow,
        '&:hover': {
            backgroundColor: theme.palette.common.yellow,
        },
    },
    red: {
        backgroundColor: theme.palette.common.red,
        '&:hover': {
            backgroundColor: theme.palette.common.red,
        },
    },
    black: {
        backgroundColor: theme.palette.common.darkGrey,
        '&:hover': {
            backgroundColor: theme.palette.common.darkGrey,
        },
    },
    disabledButton: {
        backgroundColor: '#F9F8F4',
    },
    progressBar: {
        color: theme.palette.common.white,
    },
    loadingProgress: {
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    paddingClass: {
        paddingLeft: '20px',
        paddingRight: '20px',
    },
}));

/**
 * @typedef {Object} FilledButtonProps
 * @property {import('react').ReactNode} text
 * @property {() => void} onPress
 * @property {string} [color]
 * @property {boolean} [fullWidth]
 * @property {number | string} [height]
 * @property {number | string} [marginBottom]
 * @property {boolean} [loading]
 * @property {boolean} [disabled]
 * @property {number | string} [marginTop]
 * @property {boolean} [labelStyle]
 * @property {string} [classNameProp]
 * @property {'button' | 'submit' | 'reset'} [type]
 * @property {string} [dataTestId]
 */

/**
 * @param {FilledButtonProps} props
 * @param {import('react').ForwardedRef<HTMLButtonElement>} ref
 */
const FilledButton = (
    {
        text,
        color = undefined,
        onPress,
        fullWidth = undefined,
        height = undefined,
        marginBottom = undefined,
        loading = false,
        disabled = false,
        marginTop = undefined,
        labelStyle = false,
        classNameProp = undefined,
        type = 'button',
        dataTestId = undefined,
    },
    ref
) => {
    const classes = useStyles();

    return (
        <Button
            ref={ref}
            variant="contained"
            type={type}
            disabled={disabled}
            disableElevation
            fullWidth={fullWidth === true}
            data-testid={dataTestId}
            onClick={() => {
                if (!loading) {
                    onPress();
                }
            }}
            classes={{
                root: clsx(classes.findButton, {
                    [classes.mediumBrown]: color === 'mediumBrown',
                    [classes.black]: color === 'black',
                    [classes.red]: color === 'red',
                    [classes.yellow]: !color || (color !== 'mediumBrown' && color !== 'black' && color !== 'red'),
                }),
                label: clsx(
                    color === 'black' || color === 'red' ? classes.whiteText : classes.buttonText,
                    labelStyle ? classes.paddingClass : null,
                    classNameProp || null
                ),
                disabled: classes.disabledButton,
            }}
            style={{
                height: height || null,
                opacity: !disabled ? 1 : 0.4,

                marginBottom: marginBottom,
                marginTop: marginTop,
            }}
        >
            {loading === false ? (
                text
            ) : (
                <div className={classes.loadingProgress}>
                    <CircularProgress className={classes.progressBar} size={40} />
                </div>
            )}
        </Button>
    );
};

export default forwardRef(FilledButton);
